import type { Vector3, Quaternion, Body as BodyNode } from "../types/scene";

/**
 * Quaternion math utilities for coordinate system transformations
 */

/**
 * Multiply two quaternions (Hamilton product)
 * @param q1 First quaternion [w, x, y, z]
 * @param q2 Second quaternion [w, x, y, z]
 * @returns Product quaternion [w, x, y, z]
 */
export function multiplyQuaternions(q1: Quaternion, q2: Quaternion): Quaternion {
  const [w1, x1, y1, z1] = q1;
  const [w2, x2, y2, z2] = q2;

  return [
    w1 * w2 - x1 * x2 - y1 * y2 - z1 * z2, // w
    w1 * x2 + x1 * w2 + y1 * z2 - z1 * y2, // x
    w1 * y2 - x1 * z2 + y1 * w2 + z1 * x2, // y
    w1 * z2 + x1 * y2 - y1 * x2 + z1 * w2, // z
  ];
}

/**
 * Conjugate of a quaternion
 * @param q Quaternion [w, x, y, z]
 * @returns Conjugate quaternion [w, -x, -y, -z]
 */
export function conjugateQuaternion(q: Quaternion): Quaternion {
  return [q[0], -q[1], -q[2], -q[3]];
}

/**
 * Rotate a vector by a quaternion
 * @param v Vector to rotate [x, y, z]
 * @param q Rotation quaternion [w, x, y, z]
 * @returns Rotated vector [x, y, z]
 */
export function rotateVectorByQuaternion(v: Vector3, q: Quaternion): Vector3 {
  // Convert vector to quaternion [0, x, y, z]
  const vQuat: Quaternion = [0, v[0], v[1], v[2]];

  // Rotate: q * v * q^-1
  const qConj = conjugateQuaternion(q);
  const rotatedQuat = multiplyQuaternions(multiplyQuaternions(q, vQuat), qConj);

  return [rotatedQuat[1], rotatedQuat[2], rotatedQuat[3]];
}

/**
 * Transform a joint axis through a base coordinate transformation
 * @param jointAxis Joint axis in local coordinates [x, y, z]
 * @param baseQuat Base body's quaternion rotation [w, x, y, z]
 * @returns Transformed axis [x, y, z]
 */
export function transformJointAxis(jointAxis: Vector3, baseQuat: Quaternion): Vector3 {
  return rotateVectorByQuaternion(jointAxis, baseQuat);
}

/**
 * Determine the transformation needed for a joint value based on coordinate system change
 * @param jointAxis Joint axis in world coordinates (after base transformation)
 * @param originalAxis Joint axis in local coordinates (before transformation)
 * @returns Transformation type: 'none', 'negate', or 'complex'
 */
export function getJointValueTransformation(
  jointAxis: Vector3,
  originalAxis: Vector3
): "none" | "negate" | "complex" {
  // For revolute joints, the transformation depends on how the axis changes

  // If the axis direction is inverted (sign flip), we need to negate the joint value
  const [x1, y1, z1] = originalAxis;
  const [_x2, y2, z2] = jointAxis; // x2 intentionally unused - X-axis joints don't need transformation check

  // Check if the axis has been mirrored (which would require negating joint values)
  // This happens when the base rotation causes a reflection in the coordinate system

  // For Z-axis rotations (like UR5e's 180° Z rotation):
  // - Original Z-axis becomes -Z-axis -> negate
  // - Original Y-axis becomes -Y-axis -> negate
  // - Original X-axis stays X-axis -> no change

  const tolerance = 1e-6;

  // Check if this is primarily a Z-axis joint
  if (Math.abs(z1) > Math.abs(x1) && Math.abs(z1) > Math.abs(y1)) {
    // Z-axis joint: check if Z component has flipped sign
    if (z1 * z2 < -tolerance) {
      return "negate";
    }
  }

  // Check if this is primarily a Y-axis joint
  if (Math.abs(y1) > Math.abs(x1) && Math.abs(y1) > Math.abs(z1)) {
    // Y-axis joint: check if Y component has flipped sign
    if (y1 * y2 < -tolerance) {
      return "negate";
    }
  }

  // Check if this is primarily an X-axis joint
  if (Math.abs(x1) > Math.abs(y1) && Math.abs(x1) > Math.abs(z1)) {
    // X-axis joint: usually no transformation needed
    return "none";
  }

  // For more complex transformations, we'd need full matrix analysis
  // For now, default to no transformation
  return "none";
}

/**
 * Apply coordinate transformation to a joint value
 * @param jointValue Original joint value
 * @param transformation Transformation type
 * @returns Transformed joint value
 */
export function applyJointValueTransformation(
  jointValue: number,
  transformation: "none" | "negate" | "complex"
): number {
  switch (transformation) {
    case "negate":
      return -jointValue;
    case "none":
    case "complex":
    default:
      return jointValue;
  }
}

/**
 * Extract base body quaternion from robot model
 * @param bodies Robot body tree
 * @returns Base quaternion or identity if not found
 */
export function getBaseQuaternion(bodies: BodyNode[]): Quaternion {
  // Find the root body (should be the first body in the worldbody)
  // In MJCF, the base body is typically the first body in the worldbody
  if (bodies.length === 0) {
    return [1, 0, 0, 0]; // Identity quaternion
  }

  const baseBody = bodies[0];
  return baseBody.orientation ?? [1, 0, 0, 0];
}

/**
 * Create a coordinate transformation configuration for a robot
 * @param bodies Robot body tree
 * @param joints Robot joints
 * @returns Transformation configuration
 */
export function createCoordinateTransformConfig(
  bodies: BodyNode[],
  joints: Array<{ name: string; axis: Vector3 }>
) {
  const baseQuat = getBaseQuaternion(bodies);

  // Check if base has a non-identity rotation
  const isIdentity = baseQuat.every((val, idx) =>
    idx === 0 ? Math.abs(val - 1) < 1e-6 : Math.abs(val) < 1e-6
  );

  if (isIdentity) {
    // No transformation needed
    return {
      needsTransform: false,
      baseQuat,
      jointTransforms: {} as Record<string, "none" | "negate" | "complex">,
    };
  }

  // Create transformation mapping for each joint
  const jointTransforms: Record<string, "none" | "negate" | "complex"> = {};

  joints.forEach((joint) => {
    const transformedAxis = transformJointAxis(joint.axis, baseQuat);
    const transformType = getJointValueTransformation(transformedAxis, joint.axis);
    jointTransforms[joint.name] = transformType;
  });

  return {
    needsTransform: true,
    baseQuat,
    jointTransforms,
  };
}
